import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler, UseFormRegister, Controller } from 'react-hook-form';
import {
  Container,
  Row,
  Col,
  Form,
  FormCheck,
  Button,
  Stack,
  Accordion,
  DropdownButton,
  ButtonGroup,
  Dropdown,
} from 'react-bootstrap';

import { AccessLevel, BefattningDto, PermissionType, RestrictedSearchSettingsDto, UserDto } from "models";
import { dateToInputDate, isValidDate } from 'lib/DateTimeUtils';
import { useFormDirtyCheck } from 'components/Common/useFormDirtyCheck';
import { TextBoxCsv } from './TextBoxCsv';
import { t, translateEnum } from 'components/Common/Translations';
import { RoleType } from './RoleType';
import { getStandardRights } from './getStandardRights';

let permissionTypeStrToEnum: Record<string, PermissionType> = {
}
const permissionTypeKeys = Object.keys(PermissionType)
// console.log("AccessControlForm static permissionTypeKeys", permissionTypeKeys);
permissionTypeKeys.forEach((key, index) => {
  let permissionTypeStr: string = '';
  permissionTypeStr = PermissionType[index];

  permissionTypeStrToEnum[permissionTypeStr] = index;
  // console.log(`${key} has index ${index}`)
})
// console.log("AccessControlForm static", permissionTypeStrToEnum);

interface FormData {
  permissions: Record<string, boolean>;
  fromDate: Date | null;
  toDate: Date | null;
  restrictedSearchSettings: RestrictedSearchSettingsDto;
}

interface AccessControlFormProps {
  initialBefattning?: BefattningDto;
  onSubmitBefattning: (befattning: BefattningDto) => void;
  id: string;
  onFormIsChanged?: (hasChanges: boolean, updatedModel: BefattningDto) => void;
  onDeleteBefattning: (befattning: BefattningDto) => void;
}

interface PermissionTypeCheckboxProps {
  permissionType: PermissionType;
  register: UseFormRegister<FormData>;
  id: string;
  label?: string;
}

function PermissionTypeCheckbox(props: PermissionTypeCheckboxProps) {
  const permissionTypeStr = PermissionType[props.permissionType];
  const idStr = `permissionTypeCheckbox.${props.id}.${permissionTypeStr}`;
  return (<FormCheck name={permissionTypeStr} label={props.label ?? translateEnum(props.permissionType)}
    id={idStr} {...props.register(`permissions.${permissionTypeStr}`)} />);
}


function GetFormData(befattning: BefattningDto): FormData {
  let result = {} as FormData;

  result.permissions = {};
  // Reset to false for all items in the enum
  Object.keys(PermissionType).forEach((key, index) => {
    result.permissions[key] = false;
  });
  // Set to true for the items in the array
  befattning.permissions.forEach(element => {
    result.permissions[PermissionType[element]] = true;
  });

  result.fromDate = befattning.fromDate;
  result.toDate = befattning.toDate;
  result.restrictedSearchSettings = befattning.restrictedSearchSettings ?? { supplierList: [], accountList: [], costCenterList: [], object3List: [], projectList: [], onlyFinallyAttested: false, accessLevel: null };

  return result;
}

const AccessControlForm: React.FC<AccessControlFormProps> = ({ initialBefattning, onSubmitBefattning, id, onFormIsChanged, onDeleteBefattning }) => {
  const [initialFormData, setInitialFormData] = useState(GetFormData(initialBefattning));
  
  useEffect(() => {
    const newFormData = GetFormData(initialBefattning);
    setInitialFormData(newFormData)
  }, [initialBefattning]);

  const { register, handleSubmit, reset, watch, control, getValues, setValue, formState: { errors } } = useForm<FormData>({
    defaultValues: initialFormData,
  });

  const getDataToSubmit = (data: FormData): BefattningDto => {
    const selectedPermissions: PermissionType[] = [];
    for (const key in data.permissions) {
      if (data.permissions[key]) {
        // console.log("permissions", key);
        selectedPermissions.push(permissionTypeStrToEnum[key]);
      }
    }

    const updatedBefattning: BefattningDto = {
      id: initialBefattning?.id || 0, // Or handle ID creation as needed
      user: initialBefattning?.user || { id: 0 } as UserDto, // Provide user information
      permissions: selectedPermissions,
      fromDate: data.fromDate,
      toDate: data.toDate,
      restrictedSearchSettings: data.restrictedSearchSettings,
    };
    return updatedBefattning;
  }

  const onSubmit: SubmitHandler<FormData> = (data) => {
    const updatedBefattning = getDataToSubmit(data);
    onSubmitBefattning(updatedBefattning);
  };

  const handleReset = () => {
    reset(GetFormData(initialBefattning));
  }

  const handleDelete = () => {
    if (initialBefattning?.id) {
      onDeleteBefattning(initialBefattning);
    }
  }

  const formDataHasChanged = useFormDirtyCheck(control, initialFormData);

  useEffect(() => {
    const formValues = getValues();
    onFormIsChanged(formDataHasChanged, getDataToSubmit(formValues));
  }, [formDataHasChanged]);

  const hasDates = (!!initialBefattning.fromDate || !!initialBefattning.fromDate);
  const isSaved = initialBefattning?.id > 0;

  async function setStandardRights(roleType: RoleType) {
    let permissions = await getStandardRights(roleType)
    permissions.forEach(x => setValue(`permissions.${PermissionType[x]}`, true))
  }

  // När befattningen är nyligen tillagd, så är permissions listan tom. 
  // Då kan vi agera specifikt när man kryssar i ankomstregistrerare exv.
  const isEmpty = initialBefattning?.permissions.length == 0;
  const ankomstRegistrerare = watch('permissions.User')
  const attestant = watch('permissions.Attestant')
  useEffect(() => {
    if (ankomstRegistrerare) {
      const permissions = [PermissionType.AktivaFakturor, PermissionType.SearchInvoices, PermissionType.ChangeInvoiceInformation,
      PermissionType.ChangeKontering,
      PermissionType.BytaAttestkedja,
      PermissionType.Suppliers,
      PermissionType.Attestkedjemall,
      PermissionType.Kontoplan,
      PermissionType.Kostnadsstallen,
      PermissionType.Projekt,
      PermissionType.Valutor,
      ]
      permissions.forEach(x => setValue(`permissions.${PermissionType[x]}`, true))
    }
    if (attestant) {
      const permissions = [PermissionType.WebUser]
      permissions.forEach(x => setValue(`permissions.${PermissionType[x]}`, true))
    }
  }, [ankomstRegistrerare, attestant]);

  return (
    <Container fluid>
      <Form onSubmit={handleSubmit(onSubmit)} key={id} id={"accesscontrolform" + id}>
        <Row>
          <Col sm="3">
            <h6>Användartyp:</h6>
            <PermissionTypeCheckbox permissionType={PermissionType.User} register={register} id={id} />
            <PermissionTypeCheckbox permissionType={PermissionType.Attestant} register={register} id={id} />
            <PermissionTypeCheckbox permissionType={PermissionType.Admin} register={register} id={id} />
            <PermissionTypeCheckbox permissionType={PermissionType.WebUser} register={register} id={id} />
            <br />
            <h6>Moduler:</h6>
            <PermissionTypeCheckbox permissionType={PermissionType.Bokforing} register={register} id={id} />
            <PermissionTypeCheckbox permissionType={PermissionType.Contracts} register={register} id={id} />
            <PermissionTypeCheckbox permissionType={PermissionType.FilArkiv} register={register} id={id} />

          </Col>

          <Col sm="3">
            <h6>Arbeta med:</h6>

            <PermissionTypeCheckbox permissionType={PermissionType.AktivaFakturor} register={register} id={id} />
            <PermissionTypeCheckbox permissionType={PermissionType.SearchInvoices} register={register} id={id} />

            <PermissionTypeCheckbox permissionType={PermissionType.Betalningar} register={register} id={id} />

          </Col>

          <Col sm="3">
            <h6>Redigera fakturor:</h6>
            <PermissionTypeCheckbox permissionType={PermissionType.ChangeInvoiceInformation} register={register} id={id} />
            <PermissionTypeCheckbox permissionType={PermissionType.ChangeKontering} register={register} id={id} />
            <PermissionTypeCheckbox permissionType={PermissionType.BytaAttestkedja} register={register} id={id} />
          </Col>

          <Col sm="3">
            <h6>Register:</h6>
            <PermissionTypeCheckbox permissionType={PermissionType.Suppliers} register={register} id={id} />
            <PermissionTypeCheckbox permissionType={PermissionType.Kontoplan} register={register} id={id} />
            <PermissionTypeCheckbox permissionType={PermissionType.Attestkedjemall} register={register} id={id} />
            <PermissionTypeCheckbox permissionType={PermissionType.Kostnadsstallen} register={register} id={id} />
            <PermissionTypeCheckbox permissionType={PermissionType.Projekt} register={register} id={id} />
            <PermissionTypeCheckbox permissionType={PermissionType.Valutor} register={register} id={id} />
          </Col>
        </Row>


        <Accordion className='mb-3 mt-1' defaultActiveKey={hasDates ? "0" : null}>
          <Accordion.Item eventKey="0">
            <Accordion.Header>{t('Tidsbegränsning')}</Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col>
                  <Form.Group className="mb-3" controlId={`formFromDate.${id}`}>
                    <Form.Label>Från och med datum</Form.Label>
                    <Controller control={control} name="fromDate"
                      defaultValue={null}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <Form.Control
                          onChange={(raw) => {
                            const tmp = raw.target.value;
                            const newValue = new Date(tmp);
                            if (!isValidDate(newValue))
                              return onChange(null);
                            return onChange(newValue);
                          }}
                          value={dateToInputDate(value) || ""}
                          type="date"
                          className={`${errors.fromDate ? 'is-invalid' : ''}`}
                        />
                      )}
                    />
                  </Form.Group>
                </Col>

                <Col>
                  <Form.Group className="mb-3" controlId={`formToDate.${id}`}>
                    <Form.Label>Till och med datum</Form.Label>
                    <Controller control={control} name="toDate"
                      defaultValue={null}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <Form.Control
                          onChange={(raw) => {
                            const tmp = raw.target.value;
                            const newValue = new Date(tmp);
                            if (!isValidDate(newValue))
                              return onChange(null);
                            return onChange(newValue);
                          }}
                          value={dateToInputDate(value) || ""}
                          type="date"
                          className={`${errors.toDate ? 'is-invalid' : ''}`}
                        />
                      )}
                    />
                  </Form.Group>
                </Col>
              </Row>

            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="1">
            <Accordion.Header>{t('Begränsad sökning')}</Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col sm={6}>
                  <TextBoxCsv label={t('Leverantörer')} name="restrictedSearchSettings.supplierList" control={control} register={register} />
                </Col>

                <Col sm={6}>
                  <TextBoxCsv label={t('KontoNr')} name="restrictedSearchSettings.accountList" control={control} register={register} />
                </Col>

                <Col sm={6}>
                  <TextBoxCsv label={t('Kostnadsställen')} name="restrictedSearchSettings.costCenterList" control={control} register={register} />
                </Col>

                <Col sm={6}>
                  <TextBoxCsv label={t('Projekt')} name="restrictedSearchSettings.projectList" control={control} register={register} />
                </Col>
              </Row>

              <Row>
                <Col sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Accessnivå")}</Form.Label>
                    <Form.Select {...register("restrictedSearchSettings.accessLevel")}>
                      <option value=""></option>
                      <option value={AccessLevel.Normal}>Normal</option>
                      <option value={AccessLevel.Förhöjd}>Förhöjd</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Check id={`${id}.onlyFinallyAttested`}
                      type="checkbox"
                      label={t("Endast slutattestade")}
                      {...register("restrictedSearchSettings.onlyFinallyAttested")}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        <Stack direction="horizontal" gap={2}>
          <Button variant="primary" type="submit" disabled={!formDataHasChanged}>
            Spara behörigheter
          </Button>
          <div className="vr"></div>
          <Button variant="secondary" type="button" onClick={handleReset} className="ml-2" disabled={!formDataHasChanged}>
            Återställ
          </Button>

          {isSaved && (
            <Button variant="danger" type="button" onClick={handleDelete} className="ml-2" >
              Ta bort
            </Button>
          )}

          <DropdownButton
            as={ButtonGroup}
            key="up"
            drop="up"
            variant="secondary"
            title={`Standardrättigheter`}
          >
            <Dropdown.Item eventKey="1" onClick={() => setStandardRights(RoleType.AnkomstRegistrerare)}>Ankomstregistrerare</Dropdown.Item>
            <Dropdown.Item eventKey="2" onClick={() => setStandardRights(RoleType.Attestant)}>Attestant</Dropdown.Item>
            <Dropdown.Item eventKey="3" onClick={() => setStandardRights(RoleType.Revisor)}>Revisor</Dropdown.Item>
          </DropdownButton>

        </Stack>
      </Form>
    </Container >
  );
};

export default AccessControlForm;
